import React, { useState, useEffect } from "react";
import BreadCrumbs from "../../../components/BreadCrumbs";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
} from "react-bootstrap";
import { Form, Row } from "reactstrap";
import IMGUploadComponent from "../../../components/IMGUploadComponent";
import ControlledRowTextFieldAddress from "../../../components/ControlledRowTextFieldAddress";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import ButtonComponent from "../../../components/ButtonComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import Map from "../../../components/LocationSelectorModal";
import { toast } from "react-toastify";
import { uploadImage } from "../../../components/imageUploadCompont";
import {
  useCreateCartMutation,
  useUploadCartLogoFileExtntionHttpsMutation,
  useLazyGetAllCartDetailsQuery,
} from "../../../redux/services/cartSearchService";
import "./index.css";
import ControlledSelect from "../../../components/ControlledSelect";
import { useNavigate } from "react-router-dom";
import {
  CART_TYPE,
  CartTypeOption,
  SUPPORTED_CHANNELS_OPTIONS,
} from "../../../constants/constans";

interface CartOption {
  value: string;
  label: string;
  isFixedLocation: boolean;
  cartType?: string; 
}

const CreateCart = () => {
  const navigate = useNavigate();

  const [uploadedImgDetails, setUploadedImgDetails] = useState<{
    imgSrc: string;
    formData: any;
    fileExtention: string;
  }>({
    imgSrc: "",
    formData: "",
    fileExtention: "",
  });

  const [showModal, setShowModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedLocation, setSelectedLocation] = useState({
    latitude: 28.7041,
    longitude: 77.1025,
  });
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [deliveryCartOptions, setDeliveryCartOptions] = useState<CartOption[]>([]);
  const [isLoadingDeliveryCarts, setIsLoadingDeliveryCarts] = useState(false);

  const [uploadCartLogoFileExtetion] =
    useUploadCartLogoFileExtntionHttpsMutation();

  const [getAllCartDetails, { data: allCartsData, isLoading: isCartsLoading }] =
    useLazyGetAllCartDetailsQuery();

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      ...uploadedImgDetails,
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtention: e.fileExtension,
    });
    clearErrors("image");
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
    watch,
  } = useForm<{
    displayName: string;
    abbreviation: string;
    cartType: CartTypeOption | null;
    address: string;
    latitude: number;
    longitude: number;
    image: string;
    supportedChannels: any[];
    deliveryCartsIds: CartOption[];
  }>({
    defaultValues: {
      displayName: "",
      abbreviation: "",
      cartType: null,
      address: "",
      latitude: 0,
      longitude: 0,
      image: "",
      supportedChannels: [],
      deliveryCartsIds: [],
    },
  });

  const cartType = watch("cartType");
  const selectedDeliveryCartsIds = watch("deliveryCartsIds");

  const isFixedCart = cartType && cartType.value?.isFixedLocation === true;

  useEffect(() => {
    const fetchDeliveryCarts = async () => {
      if (cartType && cartType.value?.isFixedLocation === true) {
        setIsLoadingDeliveryCarts(true);
        try {
          await getAllCartDetails(undefined).unwrap();
        } catch (error) {
          console.error("Failed to fetch carts:", error);
          toast.error("Failed to load delivery carts");
        } finally {
          setIsLoadingDeliveryCarts(false);
        }
      } else {
        setDeliveryCartOptions([]);
        setValue("deliveryCartsIds", []);
      }
    };

    fetchDeliveryCarts();
  }, [cartType, getAllCartDetails, setValue]);

 useEffect(() => {
  if (allCartsData && isFixedCart) {
    try {
      let carts = allCartsData;

      if (allCartsData.items && Array.isArray(allCartsData.items)) {
        carts = allCartsData.items;
      } else if (Array.isArray(allCartsData)) {
        carts = allCartsData;
      }
      
      // Update filter to check both conditions
      const deliveryCarts = carts.filter(
        (cart: any) => 
          cart.isFixedLocation === false &&
          cart.cartType?.toLowerCase() === "delivery"
      );

      const options = deliveryCarts.map((cart: any) => ({
        value: cart.id || cart.cartId,
        label:
          cart.displayName ||
          cart.name ||
          `Cart ${cart.id?.substring(0, 8) || "Unnamed"}`,
        isFixedLocation: cart.isFixedLocation || false,
        cartType: cart.cartType || "delivery", 
      }));

      setDeliveryCartOptions(options);

      if (deliveryCarts.length === 0) {
        console.log("No delivery carts found.");
      }
    } catch (error) {
      console.error("Error processing cart data:", error);
      setDeliveryCartOptions([]);
    }
  }
}, [allCartsData, isFixedCart]);

  const handleLocationSave = (
    latitude: number,
    longitude: number,
    address: string,
  ) => {
    setSelectedLocation({ latitude, longitude });
    setValue("latitude", latitude);
    setValue("longitude", longitude);
    setValue("address", address);
  };

  const handleLocationModalClose = () => {
    setShowModal(false);
  };

  const [createCart] = useCreateCartMutation();

  const onSubmit = async (data: any) => {
  if (!uploadedImgDetails.imgSrc) {
    setError("image", {
      type: "manual",
      message: "Please upload an image.",
    });
    return;
  }
  setIsSubmitLoading(true);
  try {
    const imageReference = await uploadImage(
      uploadCartLogoFileExtetion,
      uploadedImgDetails,
    );

    const supportedChannelsValues = data.supportedChannels
      ? data.supportedChannels.map((channel: any) => channel.value)
      : [];

    const deliveryCartsIds = data.deliveryCartsIds
      ? data.deliveryCartsIds.map((cart: CartOption) => cart.value)
      : [];

    const isFixedLocation = data.cartType.value?.isFixedLocation || false;
    
    // Get the cart type and capitalize the first letter
    let cartTypeValue = data.cartType.value?.type || "delivery";
    
    // Capitalize first letter of cart type
    if (cartTypeValue) {
      cartTypeValue = cartTypeValue.charAt(0).toUpperCase() + cartTypeValue.slice(1).toLowerCase();
    }

    const cartData = {
      displayName: data.displayName,
      abbreviation: data.abbreviation || "",
      cartLocation: {
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
      },
      address: data.address,
      imageReference: imageReference,
      isFixedLocation: isFixedLocation,
      cartType: cartTypeValue, // This will now be "Fixed", "Moving", or "Delivery"
      supportedChannels: supportedChannelsValues,
      deliveryCartsIds: isFixedLocation ? deliveryCartsIds : [],
    };

    const response: any = await createCart(cartData).unwrap();
    if (
      response?.originalStatus === 409 &&
      response?.data === "This Cart Name Already Used"
    ) {
      toast.error("This Cart Name Already Used");
      setIsSubmitLoading(false);
    } else {
      toast.success("Cart Created Successfully.");
      setIsSubmitLoading(false);
      navigate("/carts/carts-list");
    }
  } catch (error: any) {
    if (
      error?.originalStatus === 409 &&
      error?.data === "This Cart Name Already Used"
    ) {
      toast.error("This Cart Name Already Used");
      setIsSubmitLoading(false);
    } else {
      setIsSubmitLoading(false);
      toast.error("Could not create cart");
      console.error("Could not create cart", error);
    }
  }
};

  return (
    <div>
      <BreadCrumbs hasSubMenus={false} />
      <Card className="mt-3">
        <CardHeader className="bg-white">
          <h5>Create Cart</h5>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <ControlledRowTextField
              label={"Cart Name"}
              inputName={"displayName"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter cart name"}
              control={control}
              rules={{ required: "Cart name is required." }}
              errors={errors}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.displayName && (
                  <span className="validation-message text-danger">
                    {errors.displayName.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />
            <ControlledRowTextField
              label={"Abbreviation"}
              inputName={"abbreviation"}
              autoComplete={"off"}
              inputType={"text"}
              placeholder={"Enter abbreviation (optional)"}
              control={control}
              errors={errors}
            />
            <hr />

            <ControlledSelect
              label="Channels"
              inputName="supportedChannels"
              control={control}
              optionsList={SUPPORTED_CHANNELS_OPTIONS}
              rules={{ required: "Please select at least one channel." }}
              errors={errors}
              placeholder="Select channels"
              isMulti={true}
              isClear={true}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.supportedChannels && (
                  <span className="validation-message text-danger">
                    {errors.supportedChannels.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            <IMGUploadComponent
              imageUrl={uploadedImgDetails.imgSrc}
              onUpload={imageUploadCallback}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.image && (
                  <span className="validation-message text-danger">
                    {errors.image.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />
            
            {/* Cart Type Selection */}
            <ControlledSelect
              label="Cart Type"
              inputName="cartType"
              control={control}
              optionsList={CART_TYPE}
              rules={{ required: "Please select cart type." }}
              errors={errors}
              placeholder="Select cart type"
              isClear={true}
            />
            <Row>
              <Col lg="4">{""}</Col>
              <Col lg="8">
                {errors.cartType && (
                  <span className="validation-message text-danger ">
                    {errors.cartType.message}
                  </span>
                )}
              </Col>
            </Row>
            <hr />

            {/* Delivery Carts Selection (only shown when cart type is Fixed) */}
            {isFixedCart && (
              <>
                <ControlledSelect
                  label="Select Delivery Carts"
                  inputName="deliveryCartsIds"
                  control={control}
                  optionsList={deliveryCartOptions}
                  errors={errors}
                  placeholder={
                    isLoadingDeliveryCarts
                      ? "Loading delivery carts..."
                      : isCartsLoading
                        ? "Loading..."
                        : deliveryCartOptions.length === 0
                          ? "No delivery carts available"
                          : "Select delivery carts"
                  }
                  isMulti={true}
                  isClear={true}
                  isDisabled={isLoadingDeliveryCarts || isCartsLoading}
                />
                <Row>
                  <Col lg="4">{""}</Col>
                  <Col lg="8">
                    {isLoadingDeliveryCarts || isCartsLoading ? (
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        delivery carts...
                      </span>
                    ) : deliveryCartOptions.length === 0 ? (
                      <span className="text-warning">
                        No delivery carts found. Create delivery carts first.
                      </span>
                    ) : selectedDeliveryCartsIds &&
                      selectedDeliveryCartsIds.length > 0 ? (
                      <span className="text-success">
                        {selectedDeliveryCartsIds.length} cart(s) selected
                      </span>
                    ) : (
                      <span className="text-muted">
                        Select delivery carts that will serve this fixed location
                      </span>
                    )}
                  </Col>
                </Row>
                <hr />
              </>
            )}

            {/* Address Field (only shown for Fixed carts) */}
            {isFixedCart && (
              <>
                <ControlledRowTextFieldAddress
                  label={"Address"}
                  inputName={"address"}
                  inputType={"text"}
                  placeholder={"Select Address From Location Button"}
                  control={control}
                  setShowModal={setShowModal}
                  rules={{ required: "Please enter an address" }}
                  errors={errors}
                />
                <hr />
              </>
            )}
            
            <Row className="d-none">
              <Col lg="5">
                <ControlledRowTextField
                  label={"Latitude"}
                  inputName={"latitude"}
                  inputType={"number"}
                  placeholder={"Enter Latitude"}
                  isRequired={true}
                  control={control}
                  isDisabled={true}
                />
              </Col>
              <Col lg="5">
                <ControlledRowTextField
                  label={"Longitude"}
                  inputName={"longitude"}
                  inputType={"number"}
                  placeholder={"Enter Longitude"}
                  isRequired={true}
                  control={control}
                  isDisabled={true}
                />
              </Col>
              <Col lg="2">
                <ButtonComponent
                  icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
                  type="button"
                  onClick={() => setShowModal(true)}
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end">
              {isSubmitLoading ? (
                <ButtonComponent
                  titel="Saving..."
                  isDisabled={true}
                  icon={<FontAwesomeIcon icon={faSpinner} spin />}
                />
              ) : (
                <ButtonComponent titel="Submit" type="submit" />
              )}
            </div>
          </Form>
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Select your location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Map onSaveLocation={handleLocationSave} />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => handleLocationModalClose()}
              >
                Save
              </Button>
            </Modal.Footer>
          </Modal>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateCart;